import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ProductGrid from '../BodyComponent/ProductGrid';
import Pagination from '../BodyComponent/Pagination';
import ExplanationProduct from '../BodyComponent/ExplanationProduct';
import CustomModal from '../BodyComponent/CustomModal';
import { getApiUrl } from '../config/api';
import ProductGridSkeleton from '../BodyComponent/ProductGridSkeleton';
import { useQuery } from '@tanstack/react-query';

function SystemActivityPage() {
    // 1. 取得 URL 參數與導航
    const { activity } = useParams();
    const decodedActivity = activity ? decodeURIComponent(activity) : '';
    const [searchParams, setSearchParams] = useSearchParams();

    // 狀態管理
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState('');
    const [selectedBarcode, setSelectedBarcode] = useState('');

    // 取得目前頁碼
    const page = parseInt(searchParams.get('page')) || 1;

    // 響應式每頁顯示數量
    const updateItemsPerPage = () => {
        const width = window.innerWidth;
        setItemsPerPage(width <= 768 ? 10 : 25);
    };

    useEffect(() => {
        updateItemsPerPage();
        window.addEventListener('resize', updateItemsPerPage);
        const shouldReset = sessionStorage.getItem('resetActivityPage') === 'true';
        if (shouldReset) {
            setSearchParams({ page: '1' }, { replace: true });
            sessionStorage.removeItem('resetActivityPage');
        }
        return () => window.removeEventListener('resize', updateItemsPerPage);
    }, [setSearchParams]);

    // 用 useQuery 快取 API 請求
    const { data, isLoading, error } = useQuery({
        queryKey: ['activityProducts', decodedActivity, page, itemsPerPage],
        queryFn: async () => {
            if (!decodedActivity) return { products: [], totalPages: 0 };
            const apiUrl = getApiUrl('/orders/get_system_activity_product_content');
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    system_activity: decodedActivity,
                    page: page,
                    limit: itemsPerPage
                })
            });
            const data = await response.json();
            return {
                products: data.data.map(item => ({
                    id: item.id,
                    barcode: item.條碼,
                    product_name: item.品名,
                    remaining_stock: item.剩餘庫存,
                    visible_quantity: item.現貨系統可販售數,
                    system_price: item.現貨系統價格,
                    system_date: item.現貨系統活動,
                    producer: item.出品商,
                    series: item.系列,
                    product_type: item.類型,
                    role1: item.角色1,
                    role2: item.角色2,
                    role3: item.角色3,
                    features: item.其他,
                    specification: item.規格,
                    imageUrl: `https://cdn.jsdelivr.net/gh/WaynezProg/product_picture/all/${item.條碼}.webp`,
                    productUrl: `https://chiikawamarket.jp/products/${item.條碼}`
                })),
                totalPages: data.total_pages === 0 ? 1 : data.total_pages || 0
            };
        },
        enabled: !!decodedActivity,
        keepPreviousData: true,
        staleTime: 30 * 60 * 1000
    });
    const totalPages = data?.totalPages || 0;

    // page 合法性檢查
    useEffect(() => {
        if (!searchParams.get('page') || isNaN(page) || page < 1 || (totalPages && page > totalPages)) {
            setSearchParams({ page: '1' }, { replace: true });
        }
    }, [searchParams, totalPages, setSearchParams, page]);

    // 滾動到最上方
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [page, decodedActivity]);

    // 分頁切換
    const handlePageChange = (newPage) => {
        setSearchParams({ page: newPage.toString() });
    };

    // 使用 CustomModal 顯示圖片與加入購物車
    const openModal = (imageUrl, barcode) => {
        const img = new window.Image();
        img.src = imageUrl;
        img.onload = () => {
            setSelectedImage(imageUrl);
            setSelectedBarcode(barcode);
            setIsModalOpen(true);
        };
        img.onerror = () => {
            setSelectedImage('/404.jpg');
            setSelectedBarcode(barcode);
            setIsModalOpen(true);
        };
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedImage('');
        setSelectedBarcode('');
    };

    // 取用快取資料
    const productData = data?.products || [];

    // UI
    return (
        <div>
            <ExplanationProduct />
            <h1>{decodedActivity || '未找到活動'}</h1>
            {isLoading ? (
                <ProductGridSkeleton count={itemsPerPage} />
            ) : error ? (
                <div>資料載入失敗，請稍後再試。</div>
            ) : (
                <div>
                    <ProductGrid productData={productData} openModal={openModal} />
                    <Pagination 
                        currentPage={page} 
                        totalPages={totalPages} 
                        onPageChange={handlePageChange} 
                    />
                </div>
            )}
            <CustomModal
                isOpen={isModalOpen}
                onClose={closeModal}
                imageSrc={selectedImage}
                barcode={selectedBarcode}
            />
        </div>
    );
}

export default SystemActivityPage; 